import { Suspense, lazy, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SIGNATURE_CODE_TTL_MINUTES, type SignatureChallenge, type SignatureInput } from '@pqrs/contracts';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { CodeInput, Field } from '@/components/ui';
import { Label } from '@xiltrion/ui/label';
import { Spinner } from '@xiltrion/ui/spinner';
import { ApiError, apiGet, apiPost } from '@/api/client';
import { useSession } from '@/app/session';
import { errorMessage } from '@/features/cases/lib';
import { useCase } from '@/features/cases/api';
import { CitizenPetition } from '@/features/cases/CitizenPetition';
import { ResponseAttachmentsView } from './ResponseAttachments';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import type { TaskDetail } from './api';

const PdfPages = lazy(() => import('@/features/cases/detail/PdfPages'));

/**
 * Firmar un paso con «Requiere firma», a pantalla completa para leer sin distracciones: el documento tal como quedará,
 * la declaración de quien firma y un código de 6 dígitos: el de su app autenticadora de la suite (si la tiene activa,
 * GUIA-AGENTES §3.13) o, de respaldo, uno que le llega al correo.
 */
export function SignDialog({ task, onHide, onSign }: { task: TaskDetail; onHide: () => void; onSign: (signature: SignatureInput) => Promise<unknown> }) {
  const { me } = useSession();
  const [accepted, setAccepted] = useState(false);
  /** Si tiene la app autenticadora activa en la suite (si la suite no responde, solo el correo). */
  const hasApp = !!useQuery({ queryKey: ['tasks', task.id, 'signature-methods'], queryFn: () => apiGet<{ app: boolean }>(`/tasks/${task.id}/signature/methods`), staleTime: 60_000 }).data?.app;
  /** Con la app autenticadora por defecto si la tiene; el correo queda de respaldo (teléfono a mano o no). */
  const [chosen, setMode] = useState<'app' | 'email' | null>(null);
  const mode = chosen ?? (hasApp ? 'app' : 'email');
  const [challenge, setChallenge] = useState<SignatureChallenge | null>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { if (!challenge) return; const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, [challenge]);
  const resendIn = challenge ? Math.max(0, Math.ceil((new Date(challenge.resendAt).getTime() - now) / 1000)) : 0;
  const hasDocument = task.actionCode === 'SIGN' || task.responses.some((r) => r.status === 'APPROVED');
  const radication = task.case?.radication ?? '';
  // Lo que pidió el ciudadano, para firmar sabiendo a qué se responde (también cuando la petición vino en un archivo).
  const kase = useCase(task.caseId).data;

  const sendCode = async () => {
    setSending(true); setError(null);
    try { setChallenge(await apiPost<SignatureChallenge>(`/tasks/${task.id}/signature/code`)); setCode(''); setNow(Date.now()); }
    catch (e) { setError(errorMessage(e)); }
    finally { setSending(false); }
  };
  const sign = async () => {
    if (mode === 'email' && !challenge) return;
    setSigning(true); setError(null);
    try { await onSign(mode === 'app' ? { code } : { challengeId: challenge!.challengeId, code }); }
    catch (e) {
      setError(errorMessage(e)); setCode('');
      // Sin intentos, vencido o con el contenido cambiado: ese código ya no sirve y hay que pedir otro.
      if (e instanceof ApiError && (e.code === 'SIGNATURE_CODE_EXPIRED' || e.code === 'SIGNATURE_CONTENT_CHANGED' || (e.code === 'SIGNATURE_CODE_INVALID' && (e.details as { remaining?: number } | undefined)?.remaining === 0))) setChallenge(null);
    } finally { setSigning(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !signing) onHide(); }}>
      <DialogContent className="sm:max-w-none w-screen max-w-none h-dvh max-h-dvh rounded-none p-0 gap-0 flex flex-col lg:flex-row overflow-hidden">
        <div className="flex-1 min-h-0 overflow-auto bg-muted p-4 flex items-start justify-center">
          {hasDocument ? (
            <Suspense fallback={<div className="flex justify-center p-8"><Spinner /></div>}><PdfPages url={`/api/v1/tasks/${task.id}/signature/preview`} name="Respuesta por firmar" /></Suspense>
          ) : (
            <div className="bg-card border border-border rounded-lg p-6 max-w-xl w-full text-sm flex flex-col gap-2">
              <div className="font-semibold">{task.name}</div>
              <div className="text-muted-foreground">Radicado {radication} · {task.case?.subject}</div>
              <p className="m-0">Con tu firma queda registrado que completaste este paso.</p>
            </div>
          )}
        </div>
        <aside className="w-full lg:w-[380px] shrink-0 border-t lg:border-t-0 lg:border-l border-border bg-background p-6 flex flex-col gap-5 overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Icono nombre="mdi:draw-pen" aria-hidden /> Firmar</DialogTitle>
            <DialogDescription>{task.name}{radication ? ` · ${radication}` : ''}. Lee el documento completo antes de firmar: una vez firmado no se puede cambiar.</DialogDescription>
          </DialogHeader>

          {kase && <Plegable titulo="Petición del ciudadano" variante="borde" defaultAbierto={!kase.body.trim()}><CitizenPetition c={kase} /></Plegable>}
          {hasDocument && <ResponseAttachmentsView caseId={task.caseId} ids={task.responses.find((r) => r.status === 'APPROVED' || r.status === 'SUBMITTED')?.attachmentDocumentIds} />}

          <div className="flex items-start gap-3">
            <Checkbox id="sign-accept" checked={accepted} disabled={!!challenge} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
            <Label htmlFor="sign-accept" className="font-normal leading-snug">Leí {hasDocument ? 'la respuesta' : 'lo que voy a firmar'} y la firmo como <strong>{me?.displayName}</strong>{me?.jobTitle ? `, ${me.jobTitle}` : ''}.</Label>
          </div>

          {mode === 'app' ? (
            <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (code.length === 6) void sign(); }}>
              <div className="text-sm">Escribe el código de 6 dígitos que muestra ahora tu app autenticadora.</div>
              <Field label="Código de la app" htmlFor="sign-code"><CodeInput id="sign-code" value={code} onChange={setCode} disabled={!accepted} size="lg" /></Field>
              <Button type="submit" variant="success" disabled={!accepted || code.length !== 6 || signing}>{signing ? <Spinner /> : <Icono nombre="mdi:draw-pen" />} Firmar</Button>
              <Button type="button" variant="link" size="sm" className="self-start" onClick={() => { setMode('email'); setCode(''); setError(null); }}>No tengo el teléfono: enviar un código a mi correo</Button>
            </form>
          ) : !challenge ? (
            <div className="flex flex-col gap-2">
            <Button disabled={!accepted || sending} onClick={() => void sendCode()}>{sending ? <Spinner /> : <Icono nombre="mdi:email-fast-outline" />} Enviar código a mi correo</Button>
            {hasApp && <Button type="button" variant="link" size="sm" className="self-start" onClick={() => { setMode('app'); setCode(''); setError(null); }}>Usar mi app autenticadora</Button>}
            </div>
          ) : (
            <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (code.length === 6) void sign(); }}>
              <div className="text-sm">Enviamos un código de 6 dígitos a <strong>{challenge.sentTo}</strong>. Vence en {SIGNATURE_CODE_TTL_MINUTES} minutos.</div>
              <Field label="Código" htmlFor="sign-code"><CodeInput id="sign-code" value={code} onChange={setCode} autoFocus size="lg" /></Field>
              <Button type="submit" variant="success" disabled={code.length !== 6 || signing}>{signing ? <Spinner /> : <Icono nombre="mdi:draw-pen" />} Firmar</Button>
              <Button type="button" variant="ghost" size="sm" disabled={resendIn > 0 || sending} onClick={() => void sendCode()}>{sending ? <Spinner /> : <Icono nombre="mdi:refresh" />} {resendIn > 0 ? `Reenviar código en ${resendIn} s` : 'Reenviar código'}</Button>
              {hasApp && <Button type="button" variant="link" size="sm" className="self-start" onClick={() => { setMode('app'); setCode(''); setError(null); }}>Usar mi app autenticadora</Button>}
            </form>
          )}

          {error && <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertDescription>{error}</AlertDescription></Alert>}

          <div className="mt-auto flex flex-col gap-3">
            <p className="text-xs text-muted-foreground m-0">El código confirma que eres tú quien firma. Queda registrado con la fecha, la hora y desde dónde firmaste, y el documento lleva la constancia de la firma electrónica.</p>
            <Button variant="outline" disabled={signing} onClick={onHide}>Cancelar</Button>
          </div>
        </aside>
      </DialogContent>
    </Dialog>
  );
}
